import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { ModalDateFieldProvider } from 'src/components/UI/input/ModalDateFieldContext';
import PersonDetailsStep from './steps/PersonDetailsStep';
import ClinicalRecordsStep from './steps/ClinicalRecordsStep';
import GenomicCategoryStep from './steps/GenomicCategoryStep';
import ReportAlreadyHeldAlert, {
  reportAlreadyHeldFrom
} from 'src/components/common/ReportAlreadyHeldAlert';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  UnsavedChangeCount,
  useUnsavedFormGuard
} from 'src/components/UI/input/changed-field';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';

const NOTE_CATEGORIES = [
  'addresses',
  'appointments',
  'clinicalNotes',
  'consents',
  'contacts',
  'referrals',
  'samples',
  'tests',
  'tissuePathology',
  'treatments'
];

const NOTE_CATEGORY_LABELS = {
  contacts: 'Contacts',
  addresses: 'Addresses',
  referrals: 'Referrals',
  appointments: 'Appointments',
  clinicalNotes: 'Clinical Notes',
  consents: 'Consents',
  treatments: 'Treatments',
  tissuePathology: 'Tissue Pathology',
  samples: 'Samples',
  tests: 'Tests'
};

const STEP_ORDER = [
  'personDetails',
  'clinicalRecords',
  'diagnoses',
  'phenotypes',
  'genotypes'
];

const STEP_LABELS = {
  personDetails: 'Person Details',
  clinicalRecords: 'Clinical Records',
  diagnoses: 'Diagnoses',
  phenotypes: 'Phenotypes',
  genotypes: 'Genotypes',

  ...NOTE_CATEGORY_LABELS
};

export default function MergePersonWizard({
  open,
  retained,
  deleted,
  onClose,
  onSuccess
}) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [personDetailsUpdates, setPersonDetailsUpdates] = useState({});
  const [autoFilledKeys, setAutoFilledKeys] = useState({});
  const [transferDecisions, setTransferDecisions] = useState({});
  const [testFieldOverrides, setTestFieldOverrides] = useState({});
  const [matchedAutoFilledKeys, setMatchedAutoFilledKeys] = useState({});
  const [conflictResolutions, setConflictResolutions] = useState({});
  // The server's 409 REPORT_ALREADY_HELD, from the preview or the commit: the
  // retained family already holds one of this person's test reports, so the
  // merge cannot go ahead (see ReportAlreadyHeldAlert).
  const [refusal, setRefusal] = useState(null);
  // UNSAVED CHANGES (TGCLOUD-1902), from the person details step: the fields
  // that will change on the retained person, and those the clinician made.
  const [detailChanges, setDetailChanges] = useState({ gold: 0, edits: 0 });
  useUnsavedFormGuard(open ? detailChanges.edits : 0);
  const [unsavedPrompt, setUnsavedPrompt] = useState(false);
  // Cancel, the X, Escape and a click outside: ask while the clinician's own
  // edits would be lost.
  const requestClose = () => {
    if (detailChanges.edits > 0) setUnsavedPrompt(true);
    else onClose();
  };

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setRefusal(null);
    setPreviewData(null);
    setPersonDetailsUpdates({});
    setAutoFilledKeys({});
    setTransferDecisions({});
    setDetailChanges({ gold: 0, edits: 0 });
    setLoading(true);

    axios
      .get(APICONSTANTS.mergePersonPreview, {
        params: {
          retainedFamilyMemberId: retained.familyMemberId,
          retainedFamilyId: retained.familyId,
          deletedFamilyMemberId: deleted.familyMemberId,
          deletedFamilyId: deleted.familyId
        }
      })
      .then(({ data }) => {
        setPreviewData(data);
        setPersonDetailsUpdates(data.categories.personDetails?.retained || {});
        const decisions = {};
        ['diagnoses', 'phenotypes', 'genotypes'].forEach((k) => {
          decisions[k] = {
            selected: [],
            fieldPatches: {}
          };
        });
        NOTE_CATEGORIES.forEach((k) => {
          decisions[k] = [];
        });
        setTransferDecisions(decisions);

        const seededKeys = {};
        const seededConflicts = {};
        ['diagnoses', 'phenotypes', 'genotypes'].forEach((catKey) => {
          const cat = data.categories[catKey];
          if (!cat?.matched) return;
          cat.matched.forEach((pair) => {
            (pair.fieldDiff?.autoFillable || []).forEach((field) => {
              seededKeys[`${catKey}-${pair.retained.id}-${field}`] = true;
            });
            (pair.fieldDiff?.conflict || []).forEach((field) => {
              seededConflicts[`${catKey}-${pair.retained.id}-${field}`] =
                'retained';
            });
          });
        });
        setMatchedAutoFilledKeys(seededKeys);
        setConflictResolutions(seededConflicts);
      })
      .catch((err) => {
        const held = reportAlreadyHeldFrom(err);
        if (held) {
          setRefusal(held);
          return;
        }
        setSnack({
          status: true,
          msg: t('Failed to load merge preview'),
          severity: 'error'
        });
      })
      .finally(() => setLoading(false));
  }, [
    open,
    retained.familyMemberId,
    retained.familyId,
    deleted.familyMemberId,
    deleted.familyId,
    setSnack
  ]);

  if (!open) return null;

  const activeSteps = previewData
    ? STEP_ORDER.filter((key) => {
        if (key === 'personDetails') return true;
        if (key === 'clinicalRecords') {
          return NOTE_CATEGORIES.some((c) => {
            const cat = previewData.categories[c];
            return cat?.deleted?.length > 0 || cat?.retained?.length > 0;
          });
        }
        const cat = previewData.categories[key];
        return (
          cat?.deleted?.length > 0 ||
          cat?.retained?.length > 0 ||
          cat?.matched?.length > 0
        );
      })
    : [];

  const totalSteps = activeSteps.length + 1;
  const isSummary = activeStep === totalSteps - 1;
  const currentKey = activeSteps[activeStep];

  const handleToggle = (categoryKey, itemId) => {
    setTransferDecisions((prev) => {
      const current = prev[categoryKey];

      if (current && typeof current === 'object' && !Array.isArray(current)) {
        const selected = current.selected || [];
        return {
          ...prev,
          [categoryKey]: {
            ...current,
            selected: selected.includes(itemId)
              ? selected.filter((id) => id !== itemId)
              : [...selected, itemId]
          }
        };
      }

      const arr = current || [];
      return {
        ...prev,
        [categoryKey]: arr.includes(itemId)
          ? arr.filter((id) => id !== itemId)
          : [...arr, itemId]
      };
    });
  };

  const handleSelectAll = (categoryKey, ids) => {
    setTransferDecisions((prev) => {
      const current = prev[categoryKey];

      // Handle genomic categories (object-based)
      if (current && typeof current === 'object' && !Array.isArray(current)) {
        return {
          ...prev,
          [categoryKey]: {
            ...current,
            selected: ids
          }
        };
      }

      // Handle clinical records (array-based)
      return { ...prev, [categoryKey]: ids };
    });
  };

  const handleTestFieldOverride = (retainedTestId, fieldKey, value) => {
    setTestFieldOverrides((prev) => {
      const testOverrides = { ...(prev[retainedTestId] || {}) };
      if (value === undefined) {
        delete testOverrides[fieldKey];
      } else {
        testOverrides[fieldKey] = value;
      }
      const next = { ...prev };
      if (Object.keys(testOverrides).length === 0) {
        delete next[retainedTestId];
      } else {
        next[retainedTestId] = testOverrides;
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const patches = {};
      ['diagnoses', 'phenotypes', 'genotypes'].forEach((catKey) => {
        const cat = previewData.categories[catKey];
        if (!cat?.matched || cat.matched.length === 0) return;

        patches[catKey] = {};
        cat.matched.forEach((pair) => {
          const recordId = pair.retained.id;
          const patch = {};

          (pair.fieldDiff?.autoFillable || []).forEach((field) => {
            if (matchedAutoFilledKeys[`${catKey}-${recordId}-${field}`]) {
              patch[field] = pair.deleted[field];
            }
          });

          (pair.fieldDiff?.conflict || []).forEach((field) => {
            if (
              conflictResolutions[`${catKey}-${recordId}-${field}`] ===
              'deleted'
            ) {
              patch[field] = pair.deleted[field];
            }
          });

          if (Object.keys(patch).length > 0) {
            patches[catKey][recordId] = patch;
          }
        });
      });

      await axios.post(APICONSTANTS.mergePersonCommit, {
        retainedFamilyMemberId: retained.familyMemberId,
        retainedFamilyId: retained.familyId,
        deletedFamilyMemberId: deleted.familyMemberId,
        deletedFamilyId: deleted.familyId,
        personDetailsUpdates,
        transferDecisions,
        testFieldOverrides,
        fieldPatches: patches
      });
      setSnack({
        status: true,
        msg: t('Person merged successfully!'),
        severity: 'success'
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      // The same refusal as the preview's, for a report stored in the retained
      // family since: trying again cannot help, so it is not offered.
      const held = reportAlreadyHeldFrom(err);
      if (held) {
        setRefusal(held);
        return;
      }
      setSnack({
        status: true,
        msg: t('Merge failed. Please try again.'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    if (loading || !previewData) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6
          }}
        >
          <CircularProgress size={32} />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              ml: 2
            }}
          >
            {t('Loading merge preview…')}
          </Typography>
        </Box>
      );
    }
    if (isSummary) {
      const ALL_CATEGORIES = [
        'diagnoses',
        'phenotypes',
        'genotypes',
        ...NOTE_CATEGORIES
      ];
      const categoryBreakdown = ALL_CATEGORIES.map((key) => {
        const cat = transferDecisions[key];
        let count = 0;

        if (Array.isArray(cat)) {
          // Clinical records (simple array)
          count = cat.length;
        } else if (cat && typeof cat === 'object') {
          const selectedCount = (cat.selected || []).length;
          const patchedIds = new Set(
            Object.keys(matchedAutoFilledKeys)
              .filter((token) => token.startsWith(`${key}-`))
              .map((token) => {
                const prefix = `${key}-`;
                const rest = token.slice(prefix.length);
                const lastDash = rest.lastIndexOf('-');
                return lastDash === -1 ? null : rest.slice(0, lastDash);
              })
              .filter(Boolean)
          );
          count = selectedCount + patchedIds.size;
        }

        return { key, count };
      }).filter(({ count }) => count > 0);
      const testOverrideCount = Object.keys(testFieldOverrides).length;
      if (testOverrideCount > 0) {
        categoryBreakdown.push({
          key: '_testFieldMerges',
          count: testOverrideCount,
          label: 'Clinical record field merges'
        });
      }
      const discardBreakdown = ALL_CATEGORIES.map((key) => {
        const cat = previewData.categories[key];
        const total = cat?.deleted?.length || 0;

        let selected = 0;
        const decisions = transferDecisions[key];

        if (Array.isArray(decisions)) {
          selected = decisions.length;
        } else if (decisions && typeof decisions === 'object') {
          selected = (decisions.selected || []).length;
        }

        return { key, total, selected };
      }).filter(({ total, selected }) => total - selected > 0);
      return (
        <Box>
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2">
              {t('Ready to merge')}{' '}
              <strong>
                {`${deleted.firstName || ''} ${
                  deleted.lastName || ''
                }`.trim() || deleted.familyMemberId}
              </strong>{' '}
              ({deleted.familyMemberId}
              {t(') into')}{' '}
              <strong>
                {`${retained.firstName || ''} ${
                  retained.lastName || ''
                }`.trim() || retained.familyMemberId}
              </strong>{' '}
              ({retained.familyMemberId}).
            </Typography>
          </Alert>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3
            }}
          >
            {/* Transfer summary */}
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                borderColor:
                  categoryBreakdown.length > 0 ? 'success.light' : 'grey.300'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1
                }}
              >
                <CheckCircleOutlineIcon fontSize="small" color="success" />
                <Typography variant="subtitle2">
                  {t('Records to transfer')}
                </Typography>
              </Box>
              {categoryBreakdown.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                >
                  {categoryBreakdown.map(({ key, count, label }) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {label || t(STEP_LABELS[key])}
                      </Typography>
                      <Chip
                        label={count}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {t('No records selected for transfer.')}
                </Typography>
              )}
            </Paper>

            {/* Discard summary */}
            {discardBreakdown.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  borderColor: 'warning.light'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle2">
                    {t('Records to discard')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}
                >
                  {discardBreakdown.map(({ key, total, selected }) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {t(STEP_LABELS[key])}
                      </Typography>
                      <Chip
                        label={total - selected}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
          <Alert
            severity="error"
            variant="outlined"
            icon={<WarningAmberIcon />}
            sx={{ borderRadius: 2 }}
          >
            {t(
              "This action cannot be undone. The deleted person's record will be permanently removed."
            )}
          </Alert>
        </Box>
      );
    }
    if (currentKey === 'personDetails') {
      return (
        <PersonDetailsStep
          retained={previewData.categories.personDetails.retained}
          deleted={previewData.categories.personDetails.deleted}
          updates={personDetailsUpdates}
          onUpdate={setPersonDetailsUpdates}
          autoFilledKeys={autoFilledKeys}
          onAutoFilled={setAutoFilledKeys}
          onChangeCount={(gold, edits) =>
            setDetailChanges((prev) =>
              prev.gold === gold && prev.edits === edits
                ? prev
                : { gold, edits }
            )
          }
        />
      );
    }
    if (currentKey === 'clinicalRecords') {
      const sections = NOTE_CATEGORIES.map((key) => ({
        key,
        label: NOTE_CATEGORY_LABELS[key],
        retained: previewData.categories[key]?.retained || [],
        deleted: previewData.categories[key]?.deleted || [],
        selected: transferDecisions[key] || []
      })).filter((s) => s.deleted.length > 0 || s.retained.length > 0);
      return (
        <ClinicalRecordsStep
          sections={sections}
          retainedName={
            `${retained.firstName || ''} ${retained.lastName || ''}`.trim() ||
            retained.familyMemberId
          }
          deletedName={
            `${deleted.firstName || ''} ${deleted.lastName || ''}`.trim() ||
            deleted.familyMemberId
          }
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          testFieldOverrides={testFieldOverrides}
          onTestFieldOverride={handleTestFieldOverride}
        />
      );
    }
    const cat = previewData.categories[currentKey];
    return (
      <GenomicCategoryStep
        categoryKey={currentKey}
        label={t(STEP_LABELS[currentKey])}
        matched={cat?.matched || []}
        retained={cat?.retained || []}
        deleted={cat?.deleted || []}
        selected={transferDecisions[currentKey]?.selected || []}
        onToggle={(id) => handleToggle(currentKey, id)}
        onSelectAll={(ids) => handleSelectAll(currentKey, ids)}
        autoFilledKeys={matchedAutoFilledKeys}
        onAutoFilledChange={setMatchedAutoFilledKeys}
        conflictResolutions={conflictResolutions}
        onConflictResolutionChange={setConflictResolutions}
        fieldOverrides={testFieldOverrides}
        onFieldOverride={handleTestFieldOverride}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3, overflow: 'hidden' } }
      }}
    >
      <UnsavedChangesDialog
        open={unsavedPrompt}
        count={detailChanges.edits}
        onCancel={() => setUnsavedPrompt(false)}
        onDiscard={() => {
          setUnsavedPrompt(false);
          onClose();
        }}
      />
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1
        }}
      >
        <MergeTypeIcon color="primary" />
        <Box>
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 700
            }}
          >
            {t('Merge Person')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.75,
              mt: 0.5
            }}
          >
            <Chip
              label={
                `${deleted.firstName || ''} ${deleted.lastName || ''}`.trim() ||
                deleted.familyMemberId
              }
              size="small"
              sx={{
                bgcolor: 'common.white',
                color: 'error.dark',
                border: '1px solid',
                borderColor: 'error.main',
                fontWeight: 600,
                '& .MuiChip-label': { px: 1 }
              }}
            />
            <ArrowForwardIcon
              sx={{
                fontSize: 16,
                color: 'common.white'
              }}
            />
            <Chip
              label={
                `${retained.firstName || ''} ${
                  retained.lastName || ''
                }`.trim() || retained.familyMemberId
              }
              size="small"
              sx={{
                bgcolor: 'common.white',
                color: 'success.dark',
                border: '1px solid',
                borderColor: 'success.main',
                fontWeight: 600,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 0, px: 0 }}>
        {!loading && previewData && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pt: 2,
              pb: 1.5,
              px: 3
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                '& .MuiStepLabel-label': { fontSize: '0.8rem' }
              }}
            >
              {activeSteps.map((key) => (
                <Step key={key}>
                  <StepLabel>{t(STEP_LABELS[key])}</StepLabel>
                </Step>
              ))}
              <Step>
                <StepLabel>{t('Summary')}</StepLabel>
              </Step>
            </Stepper>
          </Box>
        )}
        <Box
          sx={{
            minHeight: 220,
            px: 3,
            pt: 2,
            pb: 1
          }}
        >
          {refusal ? (
            <ReportAlreadyHeldAlert refusal={refusal} />
          ) : (
            <ModalDateFieldProvider>
              {renderStepContent()}
            </ModalDateFieldProvider>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={requestClose}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {t('Cancel')}
        </Button>
        {currentKey === 'personDetails' && (
          <UnsavedChangeCount count={detailChanges.gold} />
        )}
        <Box
          sx={{
            flex: 1
          }}
        />
        {activeStep > 0 && !refusal && (
          <Button
            onClick={() => setActiveStep((s) => s - 1)}
            disabled={saving}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('Back')}
          </Button>
        )}
        {!isSummary && (
          <Button
            variant="contained"
            onClick={() => setActiveStep((s) => s + 1)}
            disabled={loading}
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: 'none' }}
          >
            {t('Next')}
          </Button>
        )}
        {isSummary && (
          <Button
            variant="contained"
            color="error"
            onClick={handleSave}
            disabled={saving || !!refusal}
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <MergeTypeIcon />
              )
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? t('Merging…') : t('Confirm Merge & Delete')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
